import { DatePicker, type UseDatePickerContext } from '@ark-ui/react/date-picker';
import type React from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { colors } from '../theme/colors';

interface CalendarHeaderProps {
  onSelectToday: () => void;
  rightAction?: React.ReactNode;
}

export const CalendarHeader: React.FC<CalendarHeaderProps> = ({ onSelectToday, rightAction }) => {
  return (
    <DatePicker.Context>
      {(datePicker) => {
        return <CalendarHeaderContent onSelectToday={onSelectToday} datePicker={datePicker} rightAction={rightAction} />;
      }}
    </DatePicker.Context>
  );
};

const CalendarHeaderContent: React.FC<{
  onSelectToday: () => void;
  datePicker: UseDatePickerContext;
  rightAction?: React.ReactNode;
}> = ({ onSelectToday, datePicker, rightAction }) => {
  const prevMonthRef = useRef<number | null>(null);
  const prevYearRef = useRef<number | null>(null);

  // Don't update MonthlyData on every month navigation - only track it internally
  // This prevents unnecessary re-renders that reset the DatePicker
  // MonthlyData will be updated when the user actually selects a date in a different month
  useEffect(() => {
    const currentMonth = datePicker.visibleRange.start.month;
    const currentYear = datePicker.visibleRange.start.year;

    // Only track internally, don't update parent state during navigation
    // This prevents the query from refetching and causing a full re-render
    prevMonthRef.current = currentMonth;
    prevYearRef.current = currentYear;
  }, [datePicker.visibleRange.start.month, datePicker.visibleRange.start.year]);

  const createSelectEventHandlers = useCallback(
    () => ({
      onFocus: (e: React.FocusEvent<HTMLSelectElement>) => {
        const target = e.target as HTMLSelectElement;
        target.style.borderColor = colors.accent.blue;
        target.style.boxShadow = `0 0 0 3px ${colors.accent.blue}20`;
      },
      onBlur: (e: React.FocusEvent<HTMLSelectElement>) => {
        const target = e.target as HTMLSelectElement;
        target.style.borderColor = colors.neutral.grey[300];
        target.style.boxShadow = 'none';
      },
      onMouseEnter: (e: React.MouseEvent<HTMLSelectElement>) => {
        const target = e.target as HTMLSelectElement;
        target.style.borderColor = colors.accent.blue;
        target.style.backgroundColor = colors.neutral.grey[50];
      },
      onMouseLeave: (e: React.MouseEvent<HTMLSelectElement>) => {
        const target = e.target as HTMLSelectElement;
        target.style.borderColor = colors.neutral.grey[300];
        target.style.backgroundColor = colors.neutral.white;
      },
    }),
    []
  );

  const selectStyles: React.CSSProperties = {
    backgroundColor: colors.neutral.white,
    border: `1px solid ${colors.neutral.grey[300]}`,
    borderRadius: '6px',
    padding: '0.5rem 0.75rem',
    fontSize: '0.875rem',
    fontWeight: '600',
    color: colors.primary.background,
    cursor: 'pointer',
    outline: 'none',
    transition: 'all 0.2s ease',
    appearance: 'none' as const,
  };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '0.75rem',
      }}
    >
      <DatePicker.PrevTrigger
        style={{
          backgroundColor: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: '0.5rem',
          fontSize: '1.25rem',
          color: colors.secondary.text,
        }}
      >
        ‹
      </DatePicker.PrevTrigger>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          backgroundColor: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: '0.5rem',
          fontSize: '1rem',
          fontWeight: '600',
          color: colors.secondary.text,
        }}
      >
        <DatePicker.MonthSelect
          style={{
            ...selectStyles,
            minWidth: '100px',
          }}
          {...createSelectEventHandlers()}
        />
        <DatePicker.YearSelect
          style={{
            ...selectStyles,
            minWidth: '80px',
          }}
          {...createSelectEventHandlers()}
        />
      </div>

      <DatePicker.NextTrigger
        style={{
          backgroundColor: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: '0.5rem',
          fontSize: '1.25rem',
          color: colors.secondary.text,
        }}
      >
        ›
      </DatePicker.NextTrigger>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <button
          type="button"
          style={{
            backgroundColor: colors.accent.blue,
            color: colors.neutral.white,
            border: 'none',
            borderRadius: '6px',
            padding: '0.6rem 1rem',
            fontSize: '0.875rem',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'background-color 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = colors.accent.blue + 'CC';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = colors.accent.blue;
          }}
          onClick={onSelectToday}
        >
          Today
        </button>
        {rightAction}
      </div>
    </div>
  );
};
